import type { Router } from "vue-router";
import sdkBridge from "./sdkBridge";
import { isValidUrl } from "./index";

/**
 * 内容事件委托管理器
 * 用于处理正文内容中的 mention-user 和 post-link 点击事件
 */
export class ContentEventDelegation {
  private container: HTMLElement | null = null;
  private mentionUserClickHandler: ((event: Event) => void) | null = null;
  private postLinkClickHandler: ((event: Event) => void) | null = null;
  private containerSelector: string;
  private router: Router;

  constructor(containerSelector: string, router: Router) {
    this.containerSelector = containerSelector;
    this.router = router;
  }

  /**
   * 处理用户点击
   */
  private handleUserClick = (userId: string) => {
    this.router.push(`/user/${userId}`);
  };

  /**
   * 设置 mention-user 点击事件
   */
  private setupMentionUserClick = () => {
    // 如果已经绑定过，先清理
    if (this.mentionUserClickHandler && this.container) {
      this.container.removeEventListener(
        "click",
        this.mentionUserClickHandler,
        true
      );
      this.mentionUserClickHandler = null;
    }

    // 获取容器
    this.container = document.querySelector(
      this.containerSelector
    ) as HTMLElement;
    if (!this.container) return;

    // 创建事件委托处理器（只创建一次，后续复用）
    if (!this.mentionUserClickHandler) {
      this.mentionUserClickHandler = (event: Event) => {
        const target = event.target as HTMLElement;
        // 检查点击的元素是否是 .mention-user 或其子元素
        const mentionElement = target.closest(".mention-user");
        if (mentionElement) {
          event.preventDefault();
          event.stopPropagation();
          const userId = mentionElement.getAttribute("data-user-id");
          if (userId) {
            this.handleUserClick(userId);
          }
        }
      };
    }

    // 绑定事件（使用捕获阶段，确保能捕获到子元素的事件）
    this.container.addEventListener(
      "click",
      this.mentionUserClickHandler,
      true
    );
  };

  /**
   * 设置 post-link 点击事件
   */
  private setupPostLinkClick = () => {
    // 如果已经绑定过，先清理
    if (this.postLinkClickHandler && this.container) {
      this.container.removeEventListener(
        "click",
        this.postLinkClickHandler,
        true
      );
      this.postLinkClickHandler = null;
    }

    // 获取容器（如果还没有）
    if (!this.container) {
      this.container = document.querySelector(
        this.containerSelector
      ) as HTMLElement;
      if (!this.container) return;
    }

    // 创建事件委托处理器（只创建一次，后续复用）
    if (!this.postLinkClickHandler) {
      this.postLinkClickHandler = (event: Event) => {
        const target = event.target as HTMLElement;
        // 检查点击的元素是否是 .post-link 或其子元素
        const postLinkElement = target.closest(".post-link");
        if (postLinkElement) {
          event.preventDefault();
          event.stopPropagation();
          const linkUrl = postLinkElement.getAttribute("data-link");
          const titleText = postLinkElement.getAttribute("data-title");
          // 兼容链接配置到titleText的情况
          if (linkUrl && titleText && linkUrl === "https://") {
            // 验证 titleText 是否为有效链接
            if (isValidUrl(titleText)) {
              sdkBridge.open(titleText);
            }
            return;
          }
          // 验证并打开链接
          if (linkUrl && isValidUrl(linkUrl)) {
            sdkBridge.open(linkUrl);
          }
        }
      };
    }

    // 绑定事件（使用捕获阶段，确保能捕获到子元素的事件）
    this.container.addEventListener("click", this.postLinkClickHandler, true);
  };

  /**
   * 初始化事件绑定
   */
  public init() {
    this.setupMentionUserClick();
    this.setupPostLinkClick();
  }

  /**
   * 清理事件委托
   */
  public cleanup() {
    if (this.container) {
      if (this.mentionUserClickHandler) {
        this.container.removeEventListener(
          "click",
          this.mentionUserClickHandler,
          true
        );
        this.mentionUserClickHandler = null;
      }
      if (this.postLinkClickHandler) {
        this.container.removeEventListener(
          "click",
          this.postLinkClickHandler,
          true
        );
        this.postLinkClickHandler = null;
      }
    }
    this.container = null;
  }
}
